import { useState, useRef } from 'react'
import type { DragEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Upload, FileText, AlertCircle, CheckCircle2, X } from 'lucide-react'
import { parseALEFile, readFileAsText, isValidALEFile } from '@/lib/parsers/aleParser'
import { parseExcelFile, isValidExcelFile } from '@/lib/parsers/excelParser'
import { useCreateClips } from '@/hooks/useClips'
import { useVfxShots, useUpdateVfxShot } from '@/hooks/useVfxShots'
import { toast } from 'sonner'
import type { CreateClipInput, VfxShot } from '@/types/database'
import type { ParsedClip } from '@/lib/parsers/aleParser'

interface ClipImporterProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onImportComplete?: () => void
}

export function ClipImporter({ projectId, open, onOpenChange, onImportComplete }: ClipImporterProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [parsedClips, setParsedClips] = useState<ParsedClip[]>([])
  const [parseErrors, setParseErrors] = useState<string[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [updateExisting, setUpdateExisting] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const createClips = useCreateClips()
  const updateVfxShot = useUpdateVfxShot()

  // Fetch VFX shots for auto-linking
  const { data: vfxShotsData } = useVfxShots(projectId)

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      await handleFileSelect(files[0])
    }
  }

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      await handleFileSelect(files[0])
    }
  }

  const handleFileSelect = async (file: File) => {
    // Validate file type
    const isALE = isValidALEFile(file.name)
    const isExcel = isValidExcelFile(file.name)

    if (!isALE && !isExcel) {
      toast.error('Invalid file type. Please select an ALE or Excel file (.ale, .xlsx, .xls, .csv).')
      return
    }

    setSelectedFile(file)
    setParsedClips([])
    setParseErrors([])

    try {
      let result

      // Parse based on file type
      if (isALE) {
        const content = await readFileAsText(file)
        result = parseALEFile(content)
      } else {
        // Excel file
        result = await parseExcelFile(file)
      }

      if (result.success) {
        setParsedClips(result.clips)
        setParseErrors(result.errors)

        if (result.errors.length > 0) {
          toast.warning(`File parsed with ${result.errors.length} warning(s)`)
        } else {
          toast.success(`Successfully parsed ${result.clips.length} clips`)
        }
      } else {
        setParseErrors(result.errors)
        toast.error('Failed to parse file')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to read file')
      setSelectedFile(null)
    }
  }

  const handleImport = async () => {
    if (parsedClips.length === 0) {
      toast.error('No clips to import')
      return
    }

    setIsUploading(true)

    try {
      // Convert parsed clips to CreateClipInput format
      const clipsToCreate: CreateClipInput[] = parsedClips.map(clip => ({
        project_id: projectId,
        name: clip.name,
        tape_name: clip.tape_name,
        source_timecode: clip.source_timecode,
        duration_frames: clip.duration_frames,
        fps: clip.fps,
        source_file_path: clip.source_file_path,
        tags: clip.tags,
        custom_metadata: clip.custom_metadata,
      }))

      const result = await createClips.mutateAsync({
        clips: clipsToCreate,
        updateExisting,
      })

      // Auto-link clips to VFX shots based on shot number metadata
      const vfxShots = vfxShotsData?.shots || []
      const allImportedClips = [...result.created, ...result.updated]
      let linkedShotsCount = 0

      if (vfxShots.length > 0 && allImportedClips.length > 0) {
        for (const clip of allImportedClips) {
          // Extract shot number from various possible metadata fields
          const shotNumber = extractShotNumber(clip.custom_metadata)

          if (shotNumber) {
            // Find matching VFX shot by shot_name (case-insensitive)
            const matchingShot = vfxShots.find(
              (shot: VfxShot) => shot.shot_name.toLowerCase() === shotNumber.toLowerCase()
            )

            if (matchingShot && matchingShot.clip_id !== clip.id) {
              try {
                await updateVfxShot.mutateAsync({
                  shotId: matchingShot.id,
                  updates: { clip_id: clip.id },
                })
                linkedShotsCount++
              } catch (error) {
                console.error(`Failed to link clip ${clip.name} to VFX shot ${matchingShot.shot_name}:`, error)
              }
            }
          }
        }
      }

      // Show results
      const totalProcessed = result.created.length + result.updated.length
      const parts: string[] = []

      if (result.created.length > 0) {
        parts.push(`${result.created.length} created`)
      }
      if (result.updated.length > 0) {
        parts.push(`${result.updated.length} updated`)
      }
      if (result.skipped > 0) {
        parts.push(`${result.skipped} skipped`)
      }

      const message = `Import complete: ${parts.join(', ')}`

      // Build description with VFX linking info
      let description = ''
      if (linkedShotsCount > 0) {
        description = `Linked ${linkedShotsCount} clip${linkedShotsCount > 1 ? 's' : ''} to VFX shot${linkedShotsCount > 1 ? 's' : ''}`
      }
      if (result.duplicateClips.length > 0) {
        const duplicateInfo = `Existing clips: ${result.duplicateClips.slice(0, 3).join(', ')}${result.duplicateClips.length > 3 ? '...' : ''}`
        description = description ? `${description}. ${duplicateInfo}` : duplicateInfo
      }

      if (totalProcessed > 0) {
        toast.success(message, {
          description: description || undefined,
        })
      } else if (result.skipped > 0) {
        toast.warning(message, {
          description: `All ${result.skipped} clips already exist. Enable "Update existing clips" to merge metadata.`,
        })
      }

      // Reset and close
      handleReset()
      onOpenChange(false)
      onImportComplete?.()
    } catch (error: any) {
      toast.error(error.message || 'Failed to import clips')
    } finally {
      setIsUploading(false)
    }
  }

  const handleReset = () => {
    setSelectedFile(null)
    setParsedClips([])
    setParseErrors([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    handleReset()
    onOpenChange(false)
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  // Helper function to extract shot number from clip metadata
  const extractShotNumber = (metadata?: Record<string, any>): string | null => {
    if (!metadata) return null

    // Common field names for shot numbers in VFX submissions
    const shotNumberFields = [
      'Submissions_View::_kf_shot_number',
      '_kf_shot_number',
      'kf_shot_number',
      'shot_number',
      'shot number',
      'shot_name',
      'shot name',
      'shot',
    ]

    for (const field of shotNumberFields) {
      const value = metadata[field]
      if (value && typeof value === 'string' && value.trim() !== '') {
        return value.trim()
      }
    }

    return null
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Import Clips</DialogTitle>
          <DialogDescription>
            Upload an ALE or Excel file to import clips into your project. The file will be parsed and validated before import.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload Area */}
          {!selectedFile && (
            <div
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Drop ALE or Excel file here</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Supports .ale, .xlsx, .xls, and .csv files
              </p>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                Select File
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ale,.xlsx,.xls,.csv"
                className="hidden"
                onChange={handleFileInputChange}
              />
            </div>
          )}

          {/* Selected File Info */}
          {selectedFile && (
            <div className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleReset}
                  disabled={isUploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Parse Results */}
              {parsedClips.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="font-medium">
                      {parsedClips.length} clips ready to import
                    </span>
                  </div>

                  {/* Show sample clips */}
                  <div className="mt-2 space-y-1">
                    {parsedClips.slice(0, 3).map((clip, index) => (
                      <div key={index} className="text-xs text-muted-foreground pl-6">
                        • {clip.name}
                        {clip.tape_name && ` (${clip.tape_name})`}
                        {clip.source_timecode && ` - ${clip.source_timecode}`}
                      </div>
                    ))}
                    {parsedClips.length > 3 && (
                      <div className="text-xs text-muted-foreground pl-6">
                        ... and {parsedClips.length - 3} more
                      </div>
                    )}
                  </div>

                  {/* Show custom metadata info */}
                  {parsedClips.length > 0 && parsedClips[0].custom_metadata && Object.keys(parsedClips[0].custom_metadata).length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-xs font-medium text-muted-foreground mb-1.5">
                        Custom Metadata Columns Detected:
                      </div>
                      <div className="text-xs text-muted-foreground pl-6">
                        {Object.keys(parsedClips[0].custom_metadata).join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Parse Errors */}
              {parseErrors.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-amber-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">
                      {parseErrors.length} warning{parseErrors.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {parseErrors.slice(0, 5).map((error, index) => (
                      <div key={index} className="text-xs text-muted-foreground pl-6">
                        • {error}
                      </div>
                    ))}
                    {parseErrors.length > 5 && (
                      <div className="text-xs text-muted-foreground pl-6">
                        ... and {parseErrors.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Import Options */}
        {parsedClips.length > 0 && (
          <div className="flex items-start space-x-2 rounded-lg border p-3 bg-muted/30">
            <Checkbox
              id="update-existing"
              checked={updateExisting}
              onCheckedChange={(checked) => setUpdateExisting(checked === true)}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="update-existing"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Update existing clips
              </Label>
              <p className="text-xs text-muted-foreground">
                Merge metadata for clips that already exist in the project.
                If disabled, duplicate clips will be skipped.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={parsedClips.length === 0 || isUploading}
          >
            {isUploading ? 'Importing...' : `Import ${parsedClips.length} Clips`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
